import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { useOrg } from '../org/OrgContext.jsx'

// Admin: course create/edit — one standalone page (never a modal), with
// sections that appear/disappear based on `type` and `managed`:
//   hourly/sessions → repeat settings + schedule rows + price_mat_sess
//                     (+ t_price when hourly)
//   pay_course      → price_mat
//   managed=self    → t_rent
//   managed=bbso    → part_type / part_t / part_b with a live sum hint
// Prices are whole numbers (no decimals) — inputs use step=1 and the backend
// rejects anything non-integer.

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

const EMPTY = {
  name: '', slug: '', description: '', category_id: '', teacher_id: '',
  type: 'pay_course', managed: 'bbso', featured: false, price_show: true,
  image_url: '', capacity: '', waitlist_enabled: false,
  date_start: '', date_end: '', registration_end: '',
  price: '', repeat_type: 'weekly', repeat_period: 1,
  t_rent: '', t_price: '', part_type: 'percentage', part_t: '', part_b: '',
  price_mat: '0', price_mat_sess: '0', whatsapp_link: '',
}

export function AdminCourseFormPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { org } = useOrg()
  const isEdit = Boolean(id)

  const [v, setV] = useState(EMPTY)
  const [schedules, setSchedules] = useState([])
  const [categories, setCategories] = useState([])
  const [teachers, setTeachers] = useState([])
  const [active, setActive] = useState(true)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  const set = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setV((cur) => ({ ...cur, [k]: val }))
  }

  useEffect(() => {
    if (authLoading || !user || user.role !== 'admin') return
    const loads = [api.admin.categories.list(), api.admin.users.teachers()]
    if (isEdit) loads.push(api.admin.courses.get(id))
    Promise.all(loads)
      .then(([cats, teach, course]) => {
        setCategories(cats.categories || [])
        setTeachers(teach.teachers || [])
        if (course?.course) {
          const c = course.course
          setActive(c.active === 1)
          setSchedules((c.schedules || []).map((s) => ({
            day_of_week: s.day_of_week,
            time: String(s.time).slice(0, 5),
            notes: s.notes || '',
          })))
          setV({
            name: c.name, slug: c.slug, description: c.description || '',
            category_id: String(c.category_id), teacher_id: c.teacher_id ? String(c.teacher_id) : '',
            type: c.type, managed: c.managed,
            featured: c.featured === 1, price_show: c.price_show === 1,
            image_url: c.image_url || '', capacity: c.capacity ?? '',
            waitlist_enabled: c.waitlist_enabled === 1,
            date_start: c.date_start || '', date_end: c.date_end || '', registration_end: c.registration_end || '',
            price: String(c.price),
            repeat_type: c.repeat_type, repeat_period: c.repeat_period,
            t_rent: c.t_rent ?? '', t_price: c.t_price ?? '',
            part_type: c.part_type || 'percentage',
            part_t: c.part_t ?? '', part_b: c.part_b ?? '',
            price_mat: String(c.price_mat ?? 0), price_mat_sess: String(c.price_mat_sess ?? 0),
            whatsapp_link: c.whatsapp_link || '',
          })
        }
      })
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, id])

  // Schedule applies to hourly/sessions courses that BBSO manages; self-
  // managed courses have no schedule here (the teacher runs the course).
  const withSchedule = (v.type === 'hourly' || v.type === 'sessions') && v.managed !== 'self'

  // Live hint for the bbso split rule.
  const price = parseInt(v.price || '0', 10) || 0
  const mat = v.type === 'pay_course'
    ? (parseInt(v.price_mat || '0', 10) || 0)
    : (parseInt(v.price_mat_sess || '0', 10) || 0)
  const partSum = (parseInt(v.part_t || '0', 10) || 0) + (parseInt(v.part_b || '0', 10) || 0)
  const partTarget = v.part_type === 'percentage' ? 100 : Math.max(0, price - mat)
  const splitOk = partSum === partTarget

  async function uploadImage(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true); setError(null)
    try {
      const d = await api.uploads.upload(file)
      setV((cur) => ({ ...cur, image_url: d.url }))
    } catch (err) {
      setError(err.code || 'server')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  function addScheduleRow() {
    setSchedules((s) => [...s, { day_of_week: 0, time: '10:00', notes: '' }])
  }
  function setScheduleRow(i, k, val) {
    setSchedules((s) => s.map((row, j) => (j === i ? { ...row, [k]: val } : row)))
  }
  function removeScheduleRow(i) {
    setSchedules((s) => s.filter((_, j) => j !== i))
  }

  async function submit(e) {
    e.preventDefault()
    setBusy(true); setError(null)
    const num = (x) => (x === '' || x === null ? null : parseInt(x, 10))
    const payload = {
      name: v.name.trim(),
      slug: v.slug.trim(),
      description: v.description.trim() === '' ? null : v.description.trim(),
      category_id: num(v.category_id),
      teacher_id: num(v.teacher_id),
      type: v.type,
      managed: v.managed,
      featured: v.featured,
      price_show: v.price_show,
      image_url: v.image_url.trim() === '' ? null : v.image_url.trim(),
      capacity: num(v.capacity),
      // Waiting list is BBSO-only; the backend forces it to 0 for self-managed.
      waitlist_enabled: v.managed === 'self' ? false : v.waitlist_enabled,
      date_start: v.date_start || null,
      date_end: v.date_end || null,
      registration_end: v.registration_end || null,
      price: v.price,
      whatsapp_link: v.whatsapp_link.trim() === '' ? null : v.whatsapp_link.trim(),
    }
    if (withSchedule) {
      payload.repeat_type = v.repeat_type
      payload.repeat_period = num(v.repeat_period) || 1
      payload.schedules = schedules
      payload.price_mat_sess = v.price_mat_sess === '' ? 0 : v.price_mat_sess
      if (v.type === 'hourly') payload.t_price = v.t_price === '' ? null : v.t_price
    } else {
      payload.price_mat = v.price_mat === '' ? 0 : v.price_mat
      payload.schedules = []
    }
    if (v.managed === 'self') {
      payload.t_rent = v.t_rent === '' ? null : v.t_rent
    } else {
      payload.part_type = v.part_type
      payload.part_t = v.part_t === '' ? null : v.part_t
      payload.part_b = v.part_b === '' ? null : v.part_b
    }
    try {
      if (isEdit) await api.admin.courses.update(id, payload)
      else        await api.admin.courses.create(payload)
      navigate('/admin/courses', { replace: true })
    } catch (err) {
      setError(err.code || 'server')
      setBusy(false)
      window.scrollTo({ top: 0 })
    }
  }

  if (authLoading || loading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!user || user.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  return (
    <main className="container">
      <p><Link to="/admin/courses">← {t('admin_courses.title')}</Link></p>
      <h1>{isEdit ? t('admin_courses.edit_title', { name: v.name }) : t('admin_courses.add_title')}</h1>
      {isEdit && !active && <p className="error">{t('admin_courses.inactive_note')}</p>}

      {error && (
        <p className="error">
          {t(`admin_courses.errors.${error}`, { defaultValue: t(`auth.errors.${error}`, { defaultValue: error }) })}
        </p>
      )}

      <section className="card form-card">
        <form onSubmit={submit} noValidate>
          <label><span>{t('admin_courses.form_name')}</span>
            <input value={v.name} onChange={set('name')} required minLength={2} /></label>
          <label><span>{t('admin_courses.form_slug')} <span className="muted small">({t('admin_courses.form_slug_hint')})</span></span>
            <input value={v.slug} onChange={set('slug')} placeholder={t('admin_courses.form_slug_placeholder')} /></label>
          <label><span>{t('admin_courses.form_description')}</span>
            <textarea value={v.description} onChange={set('description')} rows={4} /></label>

          <label>
            <span>{t('admin_courses.form_category')} <Link to="/admin/categories" className="small">{t('admin_courses.manage_categories')}</Link></span>
            <select value={v.category_id} onChange={set('category_id')} required>
              <option value="">—</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>

          <label>
            <span>{t('admin_courses.form_teacher')}</span>
            <select value={v.teacher_id} onChange={set('teacher_id')}>
              <option value="">—</option>
              {teachers.filter((u) => u.role === 'teacher').map((u) => (
                <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
              ))}
            </select>
          </label>

          <label>
            <span>{t('admin_courses.form_managed')}</span>
            <select value={v.managed} onChange={set('managed')}>
              <option value="bbso">{t('admin_courses.managed_bbso')}</option>
              <option value="self">{t('admin_courses.managed_self')}</option>
            </select>
          </label>

          <label>
            <span>{t('admin_courses.form_type')}</span>
            <select value={v.type} onChange={set('type')}>
              <option value="hourly">{t('admin_courses.type_hourly')}</option>
              <option value="sessions">{t('admin_courses.type_sessions')}</option>
              <option value="pay_course">{t('admin_courses.type_pay_course')}</option>
            </select>
          </label>

          <p className="muted small">{t('admin_courses.dates_optional_hint')}</p>
          <label><span>{t('admin_courses.form_date_start')}</span>
            <input type="date" value={v.date_start || ''} onChange={set('date_start')} /></label>
          <label><span>{t('admin_courses.form_date_end')}</span>
            <input type="date" value={v.date_end || ''} onChange={set('date_end')} /></label>
          <label><span>{t('admin_courses.form_registration_end')}</span>
            <input type="date" value={v.registration_end || ''} onChange={set('registration_end')} /></label>

          <label><span>{t('admin_courses.form_price')} ({org.currency_label})</span>
            <input type="number" min="0" step="1" value={v.price} onChange={set('price')} required /></label>
          <label className="checkbox">
            <input type="checkbox" checked={v.price_show} onChange={set('price_show')} />
            <span>{t('admin_courses.form_price_show')}</span>
          </label>
          <label><span>{t('admin_courses.form_capacity')} <span className="muted small">({t('admin_courses.form_capacity_hint')})</span></span>
            <input type="number" min="1" step="1" value={v.capacity} onChange={set('capacity')} /></label>

          {/* Waiting list only makes sense for courses BBSO manages, and it
              only ever triggers when a capacity is set. */}
          {v.managed === 'bbso' && (
            <label className="checkbox">
              <input type="checkbox" checked={v.waitlist_enabled} onChange={set('waitlist_enabled')} />
              <span>
                {t('admin_courses.form_waitlist')}{' '}
                <span className="muted small">
                  ({v.capacity === '' || v.capacity === null
                    ? t('admin_courses.form_waitlist_needs_capacity')
                    : t('admin_courses.form_waitlist_hint')})
                </span>
              </span>
            </label>
          )}

          <label className="checkbox">
            <input type="checkbox" checked={v.featured} onChange={set('featured')} />
            <span>{t('admin_courses.form_featured')}</span>
          </label>

          <label>
            <span>{t('admin_courses.form_image')}</span>
            {v.image_url && (
              <img src={v.image_url} alt="" style={{ maxWidth: 320, aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 6, marginBottom: '0.5rem', display: 'block' }} />
            )}
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={uploadImage} disabled={uploading} />
            {uploading && <span className="muted small">{t('common.loading')}</span>}
          </label>

          <label><span>{t('admin_courses.form_whatsapp')} <span className="muted small">({t('admin_courses.form_whatsapp_hint')})</span></span>
            <input type="url" value={v.whatsapp_link} onChange={set('whatsapp_link')} placeholder="https://chat.whatsapp.com/…" /></label>

          {withSchedule && (
            <fieldset>
              <legend>{t('admin_courses.section_schedule')}</legend>
              <label>
                <span>{t('admin_courses.form_repeat_type')}</span>
                <select value={v.repeat_type} onChange={set('repeat_type')}>
                  <option value="weekly">{t('admin_courses.repeat_weekly')}</option>
                  <option value="monthly">{t('admin_courses.repeat_monthly')}</option>
                </select>
              </label>
              <label><span>{t('admin_courses.form_repeat_period')} <span className="muted small">({t('admin_courses.form_repeat_period_hint')})</span></span>
                <input type="number" min="1" step="1" value={v.repeat_period} onChange={set('repeat_period')} /></label>

              {schedules.map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                  <label><span>{t('admin_courses.sched_day')}</span>
                    <select value={s.day_of_week} onChange={(e) => setScheduleRow(i, 'day_of_week', Number(e.target.value))}>
                      {DAY_KEYS.map((d, di) => <option key={d} value={di}>{t(`courses.day_${d}`)}</option>)}
                    </select>
                  </label>
                  <label><span>{t('admin_courses.sched_time')}</span>
                    <input type="time" value={s.time} onChange={(e) => setScheduleRow(i, 'time', e.target.value)} /></label>
                  <label><span>{t('admin_courses.sched_note')}</span>
                    <input value={s.notes} onChange={(e) => setScheduleRow(i, 'notes', e.target.value)} maxLength={500} /></label>
                  <button type="button" className="link-button danger" onClick={() => removeScheduleRow(i)}>
                    {t('common.delete')}
                  </button>
                </div>
              ))}
              <button type="button" className="link-button" onClick={addScheduleRow}>
                {t('admin_courses.sched_add')}
              </button>
            </fieldset>
          )}

          {v.type === 'hourly' && (
            <label><span>{t('admin_courses.form_t_price')} ({org.currency_label}) <span className="muted small">({t('admin_courses.form_t_price_hint')})</span></span>
              <input type="number" min="0" step="1" value={v.t_price} onChange={set('t_price')} required /></label>
          )}

          {v.managed === 'self' && (
            <label><span>{t('admin_courses.form_t_rent')} ({org.currency_label}) <span className="muted small">({t('admin_courses.form_t_rent_hint')})</span></span>
              <input type="number" min="0" step="1" value={v.t_rent} onChange={set('t_rent')} required /></label>
          )}

          {v.managed === 'bbso' && (
            <fieldset>
              <legend>{t('admin_courses.section_split')}</legend>
              <label>
                <span>{t('admin_courses.form_part_type')}</span>
                <select value={v.part_type} onChange={set('part_type')}>
                  <option value="percentage">{t('admin_courses.part_percentage')}</option>
                  <option value="fixed">{t('admin_courses.part_fixed')}</option>
                </select>
              </label>
              <label><span>{t('admin_courses.form_part_t')}</span>
                <input type="number" min="0" step="1" value={v.part_t} onChange={set('part_t')} required /></label>
              <label><span>{t('admin_courses.form_part_b')}</span>
                <input type="number" min="0" step="1" value={v.part_b} onChange={set('part_b')} required /></label>

              {v.type === 'pay_course' ? (
                <label><span>{t('admin_courses.form_price_mat')} ({org.currency_label}) <span className="muted small">({t('admin_courses.form_price_mat_hint')})</span></span>
                  <input type="number" min="0" step="1" value={v.price_mat} onChange={set('price_mat')} /></label>
              ) : (
                <label><span>{t('admin_courses.form_price_mat_sess')} ({org.currency_label}) <span className="muted small">({t('admin_courses.form_price_mat_sess_hint')})</span></span>
                  <input type="number" min="0" step="1" value={v.price_mat_sess} onChange={set('price_mat_sess')} /></label>
              )}

              <p className={splitOk ? 'muted small' : 'error small'}>
                {v.part_type === 'percentage'
                  ? t('admin_courses.split_hint_percentage', { sum: partSum })
                  : t('admin_courses.split_hint_fixed', { sum: partSum, target: partTarget })}
              </p>
            </fieldset>
          )}

          <div className="form-actions">
            <button type="submit" disabled={busy || uploading}>
              {busy ? t('common.loading') : t('common.save')}
            </button>
            <Link to="/admin/courses" className="link-button">{t('common.cancel')}</Link>
          </div>
        </form>
      </section>
    </main>
  )
}
